import {
    Colors,
    COMPONENT_HEIGHTS,
    CYCLE_TYPOGRAPHY,
    Icon,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@amelio/ui-library';

import type { TranslationKey } from '@/i18n-key';

import { useAmelioTranslations } from '@/hooks';
import { OFFERED_REMINDER_DAYS_BEFORE_DEADLINE } from '@/utils/schedule';

export interface MilestoneReminderValues {
    reminderSevenDays: boolean;
    reminderThreeDays: boolean;
    reminderOnDeadline: boolean;
}

export type ReminderDaysBeforeDeadline = (typeof OFFERED_REMINDER_DAYS_BEFORE_DEADLINE)[number];

export const VALUE_KEY_BY_DAYS_BEFORE_DEADLINE: Readonly<
    Record<ReminderDaysBeforeDeadline, keyof MilestoneReminderValues>
> = {
    7: 'reminderSevenDays',
    3: 'reminderThreeDays',
    0: 'reminderOnDeadline',
};

export const isReminderActive = ({
    reminders,
    daysBeforeDeadline,
}: {
    reminders?: MilestoneReminderValues;
    daysBeforeDeadline: ReminderDaysBeforeDeadline;
}): boolean => Boolean(reminders?.[VALUE_KEY_BY_DAYS_BEFORE_DEADLINE[daysBeforeDeadline]]);

export const isReminderDaysReachable = ({
    daysBeforeDeadline,
    completionWindowDays,
}: {
    daysBeforeDeadline: ReminderDaysBeforeDeadline;
    completionWindowDays?: number;
}): boolean => completionWindowDays === undefined || daysBeforeDeadline <= completionWindowDays;

const OVERLINE_TYPE = `${CYCLE_TYPOGRAPHY.overline} text-[var(--color-step-rail-muted)]`;

const PILL_BASE = [
    'inline-flex items-center justify-center rounded-full border transition-all',
    `${COMPONENT_HEIGHTS.sm} px-[12px]`,
    'text-[12px]',
    'leading-[16px] font-semibold',
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-cycle-tile-ring)] focus-visible:ring-offset-2',
].join(' ');

const PILL_ACTIVE = [
    'bg-[var(--color-cycle-reminder-surface)]',
    'text-[var(--color-step-rail-violet)]',
    'border-[var(--color-cycle-reminder-border)] shadow-sm',
].join(' ');

const PILL_IDLE = [
    'bg-white text-[var(--color-step-rail-muted)]',
    'border-[var(--color-step-rail-track)]',
].join(' ');

const PILL_IDLE_HOVER = 'hover:border-[var(--color-cycle-tile-marker-idle)]';

const PILL_CLOSED = 'cursor-not-allowed opacity-50';

const getPillClasses = ({
    isActive,
    isReadOnly,
    isClosed,
}: {
    isActive: boolean;
    isReadOnly: boolean;
    isClosed: boolean;
}): string => {
    if (isActive) {
        return `${PILL_BASE} ${PILL_ACTIVE}`;
    }

    if (isClosed) {
        return `${PILL_BASE} ${PILL_IDLE} ${PILL_CLOSED}`;
    }

    if (isReadOnly) {
        return `${PILL_BASE} ${PILL_IDLE}`;
    }

    return `${PILL_BASE} ${PILL_IDLE} ${PILL_IDLE_HOVER}`;
};

const getPillLabel = ({
    t,
    daysBeforeDeadline,
}: {
    t: (key: TranslationKey, options?: Record<string, unknown>) => string;
    daysBeforeDeadline: ReminderDaysBeforeDeadline;
}): string =>
    daysBeforeDeadline === 0
        ? t('cycleEvaluationDeadlines.reminders.onDeadlineDay')
        : t('cycleEvaluationDeadlines.reminders.beforeDeadline', { count: daysBeforeDeadline });

export interface ReminderPillsProps {
    milestoneId: string;
    reminders?: MilestoneReminderValues;
    completionWindowDays?: number;
    isReadOnly?: boolean;
    onChange?: (values: MilestoneReminderValues) => void;
}

export const ReminderPills = ({
    milestoneId,
    reminders,
    completionWindowDays,
    isReadOnly = false,
    onChange,
}: ReminderPillsProps) => {
    const { t } = useAmelioTranslations();

    const groupLabel = t('cycleEvaluationDeadlines.reminders.label');

    const closedDays = OFFERED_REMINDER_DAYS_BEFORE_DEADLINE.filter(
        (daysBeforeDeadline) =>
            !isReadOnly && !isReminderDaysReachable({ daysBeforeDeadline, completionWindowDays }),
    );

    const isDayClosed = (daysBeforeDeadline: ReminderDaysBeforeDeadline): boolean =>
        closedDays.includes(daysBeforeDeadline);

    const toggle = (daysBeforeDeadline: ReminderDaysBeforeDeadline) => {
        if (isReadOnly) {
            return;
        }

        if (
            isDayClosed(daysBeforeDeadline) &&
            !isReminderActive({ reminders, daysBeforeDeadline })
        ) {
            return;
        }

        onChange?.({
            reminderSevenDays: isReminderActive({ reminders, daysBeforeDeadline: 7 }),
            reminderThreeDays: isReminderActive({ reminders, daysBeforeDeadline: 3 }),
            reminderOnDeadline: isReminderActive({ reminders, daysBeforeDeadline: 0 }),
            [VALUE_KEY_BY_DAYS_BEFORE_DEADLINE[daysBeforeDeadline]]: !isReminderActive({
                reminders,
                daysBeforeDeadline,
            }),
        });
    };

    return (
        <div
            className={'mt-[16px] flex flex-wrap items-center gap-[8px]'}
            role={'group'}
            aria-label={groupLabel}
            data-testid={`milestone-reminders-${milestoneId}`}
        >
            <span className={'mr-1 inline-flex items-center gap-1.5'}>
                <span className={OVERLINE_TYPE}>{groupLabel}</span>

                <TooltipProvider delayDuration={150}>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button
                                type={'button'}
                                className={
                                    'inline-flex rounded-full text-[var(--color-step-rail-idle)] transition-colors hover:text-[var(--color-step-rail-violet)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-cycle-tile-ring)] focus-visible:ring-offset-2'
                                }
                                aria-label={t('cycleEvaluationDeadlines.reminders.tooltip')}
                                data-testid={`milestone-reminders-info-${milestoneId}`}
                            >
                                <Icon
                                    name={'essential-info-circle'}
                                    size={'sm'}
                                    color={Colors.BASE_400}
                                />
                            </button>
                        </TooltipTrigger>

                        <TooltipContent
                            side={'top'}
                            className={'max-w-xs text-center'}
                        >
                            <p>{t('cycleEvaluationDeadlines.reminders.tooltip')}</p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            </span>

            {OFFERED_REMINDER_DAYS_BEFORE_DEADLINE.map((daysBeforeDeadline) => {
                const active = isReminderActive({ reminders, daysBeforeDeadline });
                const isClosed = isDayClosed(daysBeforeDeadline);

                return (
                    <TooltipProvider key={daysBeforeDeadline}>
                        <Tooltip delayDuration={300}>
                            <TooltipTrigger asChild>
                                <span className={'block'}>
                                    <button
                                        type={'button'}
                                        aria-pressed={active}
                                        aria-readonly={isReadOnly || undefined}
                                        aria-disabled={(isClosed && !active) || undefined}
                                        onClick={() => toggle(daysBeforeDeadline)}
                                        className={getPillClasses({
                                            isActive: active,
                                            isReadOnly,
                                            isClosed,
                                        })}
                                        data-testid={`milestone-reminder-${milestoneId}-${daysBeforeDeadline}`}
                                    >
                                        {getPillLabel({ t, daysBeforeDeadline })}
                                    </button>
                                </span>
                            </TooltipTrigger>

                            {isClosed && (
                                <TooltipContent
                                    side={'top'}
                                    className={'max-w-xs'}
                                    data-testid={`milestone-reminder-reason-${milestoneId}-${daysBeforeDeadline}`}
                                >
                                    <p>
                                        {t(
                                            'cycleEvaluationDeadlines.reminders.beforeStartUnavailable',
                                        )}
                                    </p>
                                </TooltipContent>
                            )}
                        </Tooltip>
                    </TooltipProvider>
                );
            })}
        </div>
    );
};
